/**
 * THE predicate for "does this franchise already have a subscription?".
 *
 * Mirrors the backend's own refusal, which is the authority:
 * `controllers/subscriptions/franchise.php::subscribe()` rejects with
 * 'Franquia já possui assinatura ativa' when the franchise's row is
 * 'active' or 'grace'. Anything terminal ('cancelled', 'superseded') or
 * stalled ('pending') may subscribe again, so those are NOT live.
 *
 * Lives in one module for the same reason src/utils/setupFeeStatus.ts does:
 * local copies of a billing rule drift, and the UI then offers an action the
 * backend is guaranteed to refuse — which is exactly what happened here. The
 * 360's 'Criar assinatura' button and the subscribe modal's franchise picker
 * both asked the question, neither asked it the same way, and a franchise with
 * a live subscription stayed selectable (2026-08-20).
 */

export interface SubscriptionRowLike {
  /**
   * NULL means "no subscription row at all" — see isLiveSubscriptionRow.
   * Absent (undefined) is treated the same as null.
   */
  subscription_id?: number | null
  franchise_id?: number
  status?: string | null
  /** The billing-360 payload's name for the same field. */
  effective_status?: string | null
}

/** The statuses that mean "this franchise is already subscribed". */
export const LIVE_SUBSCRIPTION_STATUSES = ['active', 'grace'] as const

/**
 * The row's status under EITHER of the two names the backend emits.
 *
 * GET /subscriptions/mine calls it `status`; the billing-360 payload calls it
 * `effective_status` (Subscriptions_Billing360SubscriptionRow has no `status`
 * field at all). Reading only `status` is how the 360's 'Criar assinatura'
 * gate came to read `undefined` on every row and therefore never gate
 * anything — a cast to `{ status?: string | null }` made it type-check.
 */
export function subscriptionRowStatus(row: SubscriptionRowLike): string {
  return String(row.status ?? row.effective_status ?? '')
}

/**
 * Is this row a subscription that is currently live?
 *
 * A row with NO subscription_id is not a subscription: /subscriptions/mine
 * appends a synthetic row per franchise that has none, and when staff granted
 * that franchise a bare grace window the synthetic row carries the PROJECTED
 * status 'grace'. Treating that as "already subscribed" locks the franchise
 * out of the only affordance that would give it a subscription.
 */
export function isLiveSubscriptionRow(row: SubscriptionRowLike | null | undefined): boolean {
  if (!row) return false
  if (row.subscription_id == null) return false
  return (LIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(subscriptionRowStatus(row))
}

/**
 * True when ANY of the franchise's rows is live.
 *
 * Rows arrive as a union of live + terminal history (GET /subscriptions/mine
 * returns every row a franchise ever had), so this must scan rather than read
 * "the" row: a franchise with one cancelled and one active row is subscribed.
 */
export function hasLiveSubscription(
  rows: SubscriptionRowLike[] | null | undefined,
  franchiseId: number,
): boolean {
  if (!rows || !franchiseId) return false
  return rows.some(
    (r) => Number(r.franchise_id) === Number(franchiseId) && isLiveSubscriptionRow(r),
  )
}
